import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, Injector, OnDestroy, Signal, signal, untracked, viewChild, ViewEncapsulation } from '@angular/core';
import { ErrorDetails } from 'src/app/services/api.services';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { DataAggregation } from '../../dataModel';
import { DisplayFormatArgs } from '../../formatting/DataFormatter';
import { AutosizingObserver } from '../../utils/AutoSizeingObserver';
import { ErrorUtils } from '../../utils/ErrorUtils';
import { VisualComponent } from '../base/Visual.component';
import { ChartVisual } from '../highcharts/ChartVisual.component';
import { DataTooltip } from '../tooltip/DataTooltipComponent';
import { Visual } from '../Visual';
import { Serialize } from '../VisualDataSerializer';
import { VisualFieldRoleType } from '../VisualFieldRoleType';
import { VisualFieldRoleTypeMap } from '../VisualFieldRoleTypeMap';
import { VisualFieldSorting } from '../VisualFieldSorting';
import { VisualHost } from '../VisualHost';
import { TextElementStyle, TextStyle, VisualStyle } from '../VisualStyle';
import { VisualType } from '../VisualType';
import { CardStyleEditorComponent } from './CardStyleEditor.component';

@Component({
  selector: 'pbir-visual__card',
  template: `
    <div class="card-visual" [hidden]="!loaded()" #cardContent>
      @let total = totalValue();
      @if (total !== undefined && total !== null) {
        <div class="d-flex justify-content-center"
             (mouseenter)="showTooltip($event)"
             (mouseleave)="hideTooltip()"
             (mousemove)="mouseMove($event)">
          <div class="card-visual__value" #totalElement>
            <span>{{total}}</span>
          </div>
        </div>
      }
    </div>

    @if (dataTooltip()) {
      <div class="pbir-datatooltip-wrapper" #tooltipWrapper [hidden]="!tooltipVisible()">
        <pbir-datatooltip [dataTooltip]="dataTooltip()"></pbir-datatooltip>
      </div>
    }
  `,

  styles: [`
  :root {
  }

  pbir-visual__card { 
    display: block;
    width: 100%;
    height: 100%;

    .card-visual {
      display: flex;
      flex-direction: column;
      padding: 6px 8px;
      background: var(--pbir-visual__background-color);
      justify-content: center;

      width: 100%;
      height: 100%;
      overflow: hidden;
      box-sizing: border-box;
    }
  }

  .pbir-datatooltip-wrapper {
    position: fixed;
    z-index: 9999;
    position: absolute;
    pointer-events: none;
    user-select: none;
  }

  .card-visual__value {
    display: inline-block;
    white-space: nowrap;
    color: var(--pbir-visual__card-measure-color);
    text-decoration: var(--pbir-visual__card-measure-text-decoration);
    font-weight: var(--pbir-visual__card-measure-font-weight);
    font-style: var(--pbir-visual__card-measure-font-style);
  }
  `],

  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CardVisualComponent  
  extends VisualComponent<CardVisual.Options, CardVisual.State, CardVisual.Style, VisualComponent.DefaultData>
  implements OnDestroy, AfterViewInit{

  readonly roles = computed(() => {
    return {
      measures: this.visual().fieldRoles.find(CardVisual.roles.measures),
    };
  });

  readonly injector = inject(Injector);
  readonly hostElement = inject(ElementRef<HTMLElement>);

  readonly measureField = computed(() => this.roles().measures.fields.at(0));

  readonly measureRendered = signal(false);
  readonly allRendered = computed(() => this.failed() || this.measureRendered());

  readonly onAllRendered = effect(() => {
    if (this.allRendered()) {
      let error: ErrorDetails = null;
      let failed = false;

      untracked(() => {
        if (this.failed()) {
          error = ErrorUtils.mergeErrorDetails(
            this.data.error(),
            this.rowsResource()?.error(),
            this.measureResource()?.error()
          );

          failed = true;
        }
      });

      this.broadcastEvent({
        type: VisualHost.EventType.RenderComplete,
        failed,
        error,
      });
    }
  });

  readonly rowsResource = computed(() => {
    return this.data.rows();
  });

  readonly measureResource = computed(() => {
    if (!this.isVisualValid())
      return null;

    let measure = this.measureField();
    
    return this.data.aggregateColumn({
      aggregation: {
        target: measure,
        aggregation: DataAggregation.Sum
      }
    });
  });

  readonly loading = computed(() =>
    this.data.loading() || this.data.fetching());

  readonly loaded = computed(() =>
    this.data.success() &&
    !!this.rowsResource()?.success() &&
    !!this.measureResource()?.success());

  readonly failed = computed(() =>
    this.data.failure() ||
    !!this.rowsResource()?.failure() ||
    !!this.measureResource()?.failure());

  readonly totalValue = computed(() => {
    if (!this.isVisualValid())
      return null;

    let measure = this.measureField();

    let rowsResource = this.data.rows();
    if (!rowsResource?.hasResult())
      return null;

    let result = rowsResource.result();
    let formatter = this.formatter(result);
    if (!result.rows?.length)
      return formatter.getValueDisplay(measure, null, this.valueFormatArgs());

    return formatter.getRowValueDisplay(result.rows[0], measure, this.valueFormatArgs());
  });

  readonly valueFormatArgs = computed(() => {
    let mesure = this.measureField();

    let numberFormat = ChartVisual.NumberFormat.options.find(x=>x.id == this.style().numberFormat());

    if (!mesure.dataType.isNumeric || !numberFormat.configureDisplay)
      return null;

    return <DisplayFormatArgs>{ display: numberFormat.configureDisplay(this.measureField().dataDisplay) };
  });

  readonly totalValuePopulated = effect(() => {
    let [totalValue, totalElement] = [this.totalValue(), this.totalElement()?.nativeElement];

    untracked(() => {
      if (!isNullOrUndefined(totalValue) && totalElement) {
        this.updateValueTextFontSize();
        this.measureRendered.set(true);
      }
      else {
        if (this.measureRendered()) {
          this.measureRendered.set(false);
          this.broadcastEvent({ type: VisualHost.EventType.RenderStart });
        }
      }
    });
  });

  readonly totalElementChangedEffect = effect(() => {
    let _ = this.totalElement();
    untracked(() => {
      this.updateValueTextFontSize();
    });
  });


  private updateValueTextFontSize() {
    let [el, h] = [this.totalElement()?.nativeElement, this.hostElement.nativeElement];
    if (!el?.textContent || !h)
      return;

    // Detach text element from current parent, and attach it to a temporary parent which inherits width/height of the card
    // Then, compute the font size to fill the parent (same calc as fitty, with small padding)

    let [containerW, containerH] = [h.scrollWidth, h.scrollHeight];
    let tempParent = document.createElement('div');
    tempParent.style.setProperty('position', 'absolute');
    tempParent.style.setProperty('left', '0');
    tempParent.style.setProperty('top', '0');
    tempParent.style.setProperty('width', `${containerW}px`);
    tempParent.style.setProperty('height', `${containerH}px`);

    let originalParent = el.parentElement;
    tempParent.append(el);

    try {
      const CONTAINER_PADDING_W = 15;
      const CONTAINER_PADDING_H = 10;

      document.body.append(tempParent);

      el.style.fontSize = `12px`;
      let textW = el.scrollWidth;
      let textH = el.scrollHeight;
      let fontSizeW = Math.min(Math.max(12, (Math.max(1, containerW - CONTAINER_PADDING_W) / textW) * 12), 999);
      let fontSizeH = Math.min(Math.max(12, (Math.max(1, containerH - CONTAINER_PADDING_H) / textH) * 12), 999);
      let fontSize  = Math.min(fontSizeW, fontSizeH);

      el.style.fontSize = `${fontSize}px`;
    }
    finally {
      originalParent.append(el);
      tempParent.remove();
    }
  }

  readonly dataTooltip = computed<DataTooltip>(() => {
    if (!this.loaded() && !this.measureField()) {
      return null;
    }

    return {
      rows: [{
        color: null,
        name: this.measureField().shortTitle,
        values: [this.totalValue()]
      }]
    };
  });

  readonly autoSize = signal(true);

  private autosizingObserver: AutosizingObserver;

  ngAfterViewInit(): void {
    this.autosizingObserver = new AutosizingObserver(
      this.autoSize,
      () => this.updateValueTextFontSize(),
      this.hostElement,
      this.injector,
      { destroyRef: this.destroyRef,
        resizeDebounceTime: 50 }
    );

    this.updateValueTextFontSize();
  }

  readonly totalElement = viewChild<ElementRef<HTMLElement>>('totalElement');

  readonly chartTooltip = computed<Highcharts.TooltipOptions>(() => {
    if (!this.isVisualValid())
      return null;
      
    let measure = this.measureField();
    let formatter = this.formatter();

    return {
      pointFormatter: function (this: Highcharts.Point) {
        return [
          measure.shortTitle,
          formatter.getValueDisplay(measure, this.y)
        ].join(' - ');
      }
    };
  });

  ngOnDestroy(): void {
    this.autosizingObserver?.destroy();
  }


  readonly tooltipVisible = signal(false);
  readonly tooltipWrapper: Signal<ElementRef<HTMLElement>> = viewChild('tooltipWrapper', { read: ElementRef });
  private _tooltipAttached = false;

  showTooltip(e: MouseEvent) {
    let wrapper = this.tooltipWrapper();
    if (!this.dataTooltip() || !wrapper) {
      this._tooltipAttached = false;
      return;
    }

    if (this.tooltipVisible())
      return;

    if (!this._tooltipAttached) {
      document.body.append(wrapper.nativeElement);
      this._tooltipAttached = true;
    }

    this.tooltipVisible.set(true);
    wrapper.nativeElement.style.setProperty('left', `${e.clientX}px`);
    wrapper.nativeElement.style.setProperty('top', `${e.clientY}px`);
  }

  hideTooltip() {
    this.tooltipVisible.set(false);
  }

  mouseMove(e: MouseEvent) {
    let wrapper = this.tooltipWrapper();
    if (!this.tooltipVisible() || !this.dataTooltip() || !wrapper) {
      this._tooltipAttached = false;
      this.tooltipVisible.set(false);
      return;
    }

    wrapper.nativeElement.style.setProperty('left', `${e.clientX}px`);
    wrapper.nativeElement.style.setProperty('top', `${e.clientY}px`);
  }

  readonly hideTooltipOnReload = effect(() => {
    if (!this.loaded() || !this.dataTooltip()) {
      untracked(() => this.hideTooltip());
    }
  });
}

export namespace CardVisual {
  export type VisualType = Visual<CardVisual.Options, CardVisual.State, CardVisual.Style>;

  export const roles = VisualFieldRoleTypeMap.create({
    measures: new VisualFieldRoleType('measures', {
      ...VisualFieldRoleType.Defaults.measures,

      displayOrder: 1,
      allowsValues: false,
      allowsMeasures: true,
      limits: { min: 1, max: 1 },
    }),
  });

  export class State extends Visual.State { }

  export class Style extends VisualStyle {
    @Serialize(TextElementStyle.SerializationOptions({
      ...TextStyle.Default,
      enabled: true,
      fontSize: null
    }))
    readonly measure = signal<TextElementStyle>(null);
    
    @Serialize({
      defaultValue: ChartVisual.NumberFormat.Compact
    })
    readonly numberFormat = signal<ChartVisual.NumberFormat>(null);

    private readonly cardStyleCss = computed(() => {
      let css: string[] = [];

      css.push(
        `--pbir-visual__card-measure-display: ${this.measure()?.enabled ? 'block' : 'none'};`);

      css.push(
        TextStyle.toCssVariables(this.measure(), '--pbir-visual__card-measure-'));

      return css;
    });

    protected buildCss(builder: string[]) {
      super.buildCss(builder);
      builder.push(...this.cardStyleCss());
    }
  }

  export interface Options extends Visual.Options {
  }

  export const type = new VisualType<Options>('card', CardVisualComponent, {
    fieldRoles: roles,
    className: 'card',
    visualOptions: {},
    canReuse: true,
    createState: o => new State(),
    createStyle: (o, fields) => new Style(),
    styleEditor: CardStyleEditorComponent,
    fieldSorting: v => VisualFieldSorting.none()(v)
  });
}
